import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { usePageSEO } from "../hooks/usePageSEO";
import { track } from "../lib/analytics";
import { BACKEND_URL } from "../lib/apiBase";
import DarkPartnerAutocomplete from "../components/DarkPartnerAutocomplete";
import {
  ArrowRight, ChevronLeft, CheckCircle2, Handshake, Coins, Users, LayoutDashboard,
  ShieldCheck, Loader2, Mail,
} from "lucide-react";

const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/Logo-transparent.png";

const BENEFITS = [
  [Coins, "Laufende Beteiligung", "Du wirst an den Performance-Fees jedes Kunden beteiligt, den du empfohlen hast — solange er dabei ist."],
  [Users, "Wir übernehmen den Rest", "Webinar, Beratung, Setup und Betreuung machen wir. Du teilst nur deinen Link — kein Verkaufen, kein Erklären."],
  [LayoutDashboard, "Eigenes Partner-Dashboard", "Live-Übersicht über deine Empfehlungen, dein Volumen und deine Provisionen — jederzeit transparent."],
  [ShieldCheck, "Kein Risiko, keine Kosten", "Keine Investition, keine Vertragslaufzeit, keine Verpflichtung. Partner sein ist ein Bonus, kein Muss."],
];

const STEPS = [
  ["01", "Bewerbung einreichen", "Formular unten ausfüllen — dauert keine 2 Minuten."],
  ["02", "Persönliche Prüfung", "Pascal prüft deine Bewerbung innerhalb von 1–2 Werktagen."],
  ["03", "Freischaltung per Mail", "Du erhältst deine Zugangsdaten und den Link zum Partner-Onboarding mit allen Videos."],
];

export default function PartnerApplication() {
  const { partnerSlug: rawSlug } = useParams();
  const partnerSlug = (rawSlug || "").trim().toLowerCase() || null;

  usePageSEO({
    title: "Alphalane-Partner werden: Bewerbung · Alphalane",
    description: "Werde Alphalane-Partner: verdiene laufend an Empfehlungen, während wir Webinar, Setup und Betreuung übernehmen. Bewirb dich in 2 Minuten.",
    canonical: "https://alphalane-tradingsystems.com/bewerbung",
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", referrer_name: "", referrer_partner_id: null, notes: "" });
  const [referrerLocked, setReferrerLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!partnerSlug) return;
    axios.get(`${API}/partners/by-slug/${encodeURIComponent(partnerSlug)}`)
      .then((r) => {
        if (r.data?.id) {
          setForm((f) => ({ ...f, referrer_name: r.data.name, referrer_partner_id: r.data.id }));
          setReferrerLocked(true);
        }
      })
      .catch(() => {});
  }, [partnerSlug]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setErr("Bitte Name, E-Mail und Telefon ausfüllen.");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/partner-signup`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        referrer_name: form.referrer_name.trim() || null,
        referrer_partner_id: form.referrer_partner_id,
        notes: form.notes.trim() || null,
      });
      track("partner_application_submit", { partner_slug: partnerSlug || null });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(1).join(".")}: ${d.msg}`).join(" · ")
        : (typeof detail === "string" ? detail : null);
      setErr(msg || "Etwas ging schief — bitte versuch es nochmal oder schreib direkt an Pascal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white overflow-x-hidden" data-testid="partner-application-page"
      style={{ background: "linear-gradient(160deg, #0B0820 0%, #131033 45%, #0B0820 100%)" }}>

      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Alphalane" className="h-8 w-auto" />
            <span className="hidden sm:inline font-display font-bold text-white">Alphalane <span className="text-white/50 font-normal text-sm">Trading Systems</span></span>
          </Link>
          <Link to="/" data-testid="application-back-home" className="text-xs sm:text-sm text-white/60 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Startseite
          </Link>
        </div>
      </header>

      {done ? (
        <section className="relative px-5 sm:px-8 py-20 sm:py-28" data-testid="application-success">
          <div className="max-w-xl mx-auto text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl">Bewerbung eingegangen. Danke!</h1>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-left space-y-4 text-sm text-slate-300">
              <div className="flex items-start gap-3"><Mail className="h-4 w-4 text-violet-400 mt-0.5 flex-shrink-0" /><span>Du erhältst gleich eine Bestätigung per E-Mail (auch im Spam-Ordner nachsehen).</span></div>
              <div className="flex items-start gap-3"><ShieldCheck className="h-4 w-4 text-violet-400 mt-0.5 flex-shrink-0" /><span>Pascal prüft deine Bewerbung persönlich — in der Regel innerhalb von <strong className="text-white">1–2 Werktagen</strong>.</span></div>
              <div className="flex items-start gap-3"><Handshake className="h-4 w-4 text-violet-400 mt-0.5 flex-shrink-0" /><span>Nach der Freischaltung bekommst du per E-Mail deine <strong className="text-white">Zugangsdaten</strong> und den Link zum <strong className="text-white">Partner-Onboarding</strong> mit allen Videos und Schritten.</span></div>
            </div>
            <Link to="/" data-testid="application-success-home"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors">
              Zur Startseite <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      ) : (
      <>
        {/* Hero */}
        <section className="relative px-5 sm:px-8 pt-16 sm:pt-24 pb-14" data-testid="application-hero">
          <div aria-hidden className="pointer-events-none absolute -top-24 left-1/4 h-96 w-96 rounded-full blur-3xl"
            style={{ background: "radial-gradient(closest-side, rgba(139,92,246,0.35), transparent 70%)" }} />
          <div className="max-w-4xl mx-auto">
            <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em] text-violet-400 mb-4">Partner-Programm</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              Empfehle Alphalane.<br />
              <span className="bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent">Verdiene laufend mit.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Du teilst deinen persönlichen Link mit Menschen, denen Alphalane wirklich helfen könnte —
              wir übernehmen Webinar, Setup und Betreuung. Für jeden Kunden, der über dich kommt,
              wirst du fair an den Performance-Fees beteiligt.
            </p>
            <div className="mt-8">
              <button type="button" data-testid="application-hero-cta"
                onClick={() => document.getElementById("bewerbungs-formular")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-bold text-sm sm:text-base shadow-lg shadow-violet-600/30 hover:shadow-violet-600/50 hover:-translate-y-0.5 transition-all">
                Jetzt bewerben <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="relative px-5 sm:px-8 py-14 sm:py-16 border-t border-white/5" data-testid="application-benefits">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Was du davon hast.</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {BENEFITS.map(([Icon, t, d]) => (
                <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <Icon className="h-6 w-6 text-violet-400 mb-3" />
                  <div className="font-display font-bold text-white text-lg">{t}</div>
                  <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ablauf */}
        <section className="relative px-5 sm:px-8 py-14 sm:py-16 border-t border-white/5" data-testid="application-steps">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-bold text-3xl sm:text-4xl">So läuft&apos;s ab.</h2>
            <div className="mt-8 space-y-2.5 max-w-2xl">
              {STEPS.map(([n, t, d]) => (
                <div key={n} className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4">
                  <span className="font-display font-bold text-violet-400 text-sm w-7 mt-0.5">{n}</span>
                  <div>
                    <div className="font-bold text-white text-sm sm:text-base">{t}</div>
                    <div className="text-sm text-slate-300">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Formular */}
        <section id="bewerbungs-formular" className="relative px-5 sm:px-8 py-14 sm:py-20 border-t border-white/5" data-testid="application-form-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display font-bold text-3xl sm:text-4xl">Deine Partner-Bewerbung.</h2>
              <p className="mt-3 text-slate-300">Dauert keine 2 Minuten — alles Weitere klären wir nach der Freischaltung.</p>
            </div>
            <form onSubmit={submit} data-testid="application-form"
              className="mt-9 max-w-lg mx-auto rounded-3xl border border-white/15 bg-white/[0.05] backdrop-blur p-6 sm:p-8 space-y-4">
              <input value={form.name} onChange={setField("name")} required data-testid="application-name-input"
                placeholder="Vor- und Nachname *" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400" />
              <input type="email" value={form.email} onChange={setField("email")} required data-testid="application-email-input"
                placeholder="Deine E-Mail *" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400" />
              <input type="tel" value={form.phone} onChange={setField("phone")} required data-testid="application-phone-input"
                placeholder="Telefon (WhatsApp) *" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400" />
              {referrerLocked ? (
                <div className="text-xs text-slate-300 rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid="application-referrer-locked">
                  Du wurdest von <strong className="text-white">{form.referrer_name}</strong> eingeladen
                </div>
              ) : (
                <DarkPartnerAutocomplete
                  value={form.referrer_name}
                  onChange={(name, pid) => setForm((f) => ({ ...f, referrer_name: name, referrer_partner_id: pid }))}
                  placeholder="Wer hat dich empfohlen? (optional)"
                  testid="application-referrer-input"
                />
              )}
              <textarea value={form.notes} onChange={setField("notes")} rows={3} data-testid="application-notes-input"
                placeholder="Nachricht an Alphalane (optional)" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400 resize-y" />
              {err && <div className="text-sm text-rose-300" data-testid="application-form-error">{err}</div>}
              <button type="submit" disabled={loading} data-testid="application-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-bold shadow-lg shadow-violet-600/30 hover:shadow-violet-600/50 transition-all disabled:opacity-60">
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Wird gesendet…</>) : (<>Bewerbung absenden <ArrowRight className="h-4 w-4" /></>)}
              </button>
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                Mit dem Absenden stimmst du zu, dass wir deine Daten zur Prüfung der Bewerbung speichern.
                Nach der Freischaltung erhältst du Zugangsdaten und den Onboarding-Link per E-Mail.
              </p>
            </form>
          </div>
        </section>
      </>
      )}

      <footer className="border-t border-white/10 px-5 sm:px-8 py-10">
        <div className="max-w-4xl mx-auto text-[11px] leading-relaxed text-slate-500">
          <p>© {new Date().getFullYear()} Alphalane Trading Systems · Moonshine Solutions GmbH · Schmitten FR, Schweiz · <Link to="/impressum" className="underline hover:text-slate-300">Impressum</Link> · <Link to="/datenschutz" className="underline hover:text-slate-300">Datenschutz</Link></p>
        </div>
      </footer>
    </div>
  );
}
